import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';

import React, { useEffect } from 'react';
import TopBar from './TopBar';
import Navbar from './Navbar';
import ProductPage from './ProductPage';
import AllProducts from './AllProducts';
import ProductFamilyPage from './ProductFamilyPage';
import Footer from './Footer';
import './App.css';
import SlugRouter from "./SlugRouter";
import Home from './pages/Home';
import Services from './pages/Services';

import Resources from './Resources';
import BEAD from './pages/BEAD';
import Partners from './pages/Partners';
import Contact from './pages/Contact';
import Pluma from './components/PlumaWidget';
import CookieBanner from './components/CookieBanner';
import AnalyticsTracker from './components/AnalyticsTracker';
import {
  sendAnalyticsHeartbeat,
  markAnalyticsSessionHidden,
} from "./analytics/siteAnalytics";

import About from './pages/About';
import Leadership from './pages/Leadership';
import News from './pages/News';
import Careers from './pages/Careers';

import Login from './pages/admin/Login';
import AdminDashboard from './pages/admin/AdminDashboard';
import RequireAuth from './components/RequireAuth';

import PortalDashboard from "./pages/portal/PortalDashboard";
import RequirePortalAuth from "./pages/portal/RequirePortalAuth";

// Blogs
import BlogPost from "./pages/blogs/BlogPost";
import BlogFiberOpticBasics from './pages/blogs/BlogFiberOpticBasics';
import BlogAIDataCenters from './pages/blogs/BlogAIDataCenters';
import BlogVSFFConnectors from './pages/blogs/BlogVSFFConnectors';
import BlogTheGofotonBrand from './pages/blogs/BlogTheGofotonBrand';
import BlogCableAssemblyQuestionsAnswered from './pages/blogs/BlogCableAssemblyQuestionsAnswered';
import BlogSkilledLaborGap from './pages/blogs/BlogSkilledLaborGap';
import BlogAITerminology from './pages/blogs/BlogAITerminology';

// Solutions
import Solutions from './pages/solutions/Solutions';
import SolutionAI from './pages/solutions/SolutionAI';
import SolutionDatacenter from './pages/solutions/SolutionDatacenter';
import SolutionCentralOffice from './pages/solutions/SolutionCentralOffice';
import SolutionOutsidePlant from './pages/solutions/SolutionOutsidePlant';
import SolutionFTTX from './pages/solutions/SolutionFTTX';
import SolutionRuralBroadband from './pages/solutions/SolutionRuralBroadband';
import SolutionWireless from './pages/solutions/SolutionWireless';
import SolutionEnterprise from './pages/solutions/SolutionEnterprise';
import SolutionMedicalImaging from './pages/solutions/SolutionMedicalImaging';
import SolutionNeuroscience from './pages/solutions/SolutionNeuroscience';
import SolutionSpecialtyOptics from './pages/solutions/SolutionSpecialtyOptics';

// Tradeshows
import ISE from './pages/tradeshows/ISE';
import AIInfraSummit from "./pages/tradeshows/AIInfraSummit";
import Tradeshow from './pages/tradeshows/Tradeshow';
import Events from './pages/tradeshows/Events';

// Landing page
import FiberShuffle from './pages/landingPages/FiberShuffle';

// Other
import PrivacyPolicy from './pages/PrivacyPolicy';
import PartNumberBuilder from "./pages/PartNumberBuilder";
import SolutionsBuilder from './SolutionsBuilder';
import AvatarTesting from './pages/AvatarTesting';
import SiteMap from './pages/SiteMap';
import NotFound from './pages/NotFound';
import HeatmapClickTracker from "./components/HeatMapClickTracker";

const PAGE_TITLES = {
  "/": "Go!Foton | Fiber Optic Connectivity Solutions",

  "/products": "Fiber Optic Products | Go!Foton",
  "/services": "Fiber Optic Network Services | Go!Foton",

  "/solutions": "Fiber Optic Network Solutions | Go!Foton",
  "/solutions/ai": "AI Data Center Fiber Optic Solutions | Go!Foton",
  "/solutions/datacenter": "Data Center Fiber Optic Solutions | Go!Foton",
  "/solutions/central-office": "Central Office Fiber Solutions | Go!Foton",
  "/solutions/outside-plant": "Outside Plant Fiber Optic Solutions | Go!Foton",
  "/solutions/fttx": "FTTx Fiber Optic Solutions | Go!Foton",
  "/solutions/rural-broadband": "Rural Broadband Fiber Solutions | Go!Foton",
  "/solutions/wireless": "Wireless Network Fiber Solutions | Go!Foton",
  "/solutions/enterprise": "Enterprise Fiber Optic Solutions | Go!Foton",
  "/solutions/medical-imaging": "Medical Imaging Optical Solutions | Go!Foton",
  "/solutions/neuroscience": "Neuroscience Optical & Photonics Solutions | Go!Foton",
  "/solutions/specialty-optics": "Specialty Optics & Photonics Solutions | Go!Foton",

  "/knowledge-center": "Fiber Optic Knowledge Center | Go!Foton",
  "/bead": "BEAD & BABA Fiber Optic Solutions | Go!Foton",
  "/partners": "Partners | Go!Foton",
  "/contact-us": "Contact Go!Foton",
  "/about": "About Go!Foton",
  "/leadership": "Leadership Team | Go!Foton",
  "/news": "News & Insights | Go!Foton",
  "/careers": "Careers at Go!Foton",

  "/events": "Events & Tradeshows | Go!Foton",
  "/part-lookup": "Part Number Lookup | Go!Foton",
  "/solutions-builder": "Fiber Optic Solutions Builder | Go!Foton",
  "/site-map": "Site Map | Go!Foton",
  "/privacy-policy": "Privacy Policy | Go!Foton",
};

function HashRedirector() {
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = window.location.pathname;
    const hash = window.location.hash;

    if (
      pathname === "/product/fiber-terminals/" &&
      hash === "#PEACOC_MMT"
    ) {
      navigate("/large-peacoc-multiport-mid-span-terminal-mmt", {
        replace: true,
      });
    }

    if (
      pathname === "/product/high-density-fiber-management/" &&
      hash === "#nemo"
    ) {
      navigate("/peacoc-nemo-any-density-patch-panel", {
        replace: true,
      });
    }

    if (
      pathname ===
        "/industry-products/peacoc-high-density-fiber-management/" &&
      hash === "#PEACOC_MMT"
    ) {
      navigate("/large-peacoc-multiport-mid-span-terminal-mmt", {
        replace: true,
      });
    }
  }, [navigate]);

  return null;
}

function App() {
  const location = useLocation();

  useEffect(() => {
    const title = PAGE_TITLES[location.pathname];

    if (title) {
      document.title = title;
    }
  }, [location.pathname]);

  useEffect(() => {
  let heartbeatTimer = null;

  const analyticsPageAllowed = () =>
    !window.location.pathname.startsWith("/admin") &&
    !window.location.pathname.startsWith("/login") &&
    !window.location.pathname.startsWith("/portal") &&
    !window.location.pathname.startsWith("/solutions-builder");

  const sendHeartbeat = () => {
    if (
      !analyticsPageAllowed() ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    sendAnalyticsHeartbeat();
  };

  const handleVisibilityChange = () => {
    if (!analyticsPageAllowed()) return;

    if (document.visibilityState === "visible") {
      sendHeartbeat();
    } else {
      markAnalyticsSessionHidden();
    }
  };

  sendHeartbeat();

  heartbeatTimer = window.setInterval(
    sendHeartbeat,
    15000
  );

  document.addEventListener(
    "visibilitychange",
    handleVisibilityChange
  );

  return () => {
    window.clearInterval(heartbeatTimer);

    document.removeEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    if (analyticsPageAllowed()) {
      markAnalyticsSessionHidden();
    }
  };
}, []);

  const isSolutionsBuilder =
    location.pathname === "/solutions-builder" ||
    location.pathname.startsWith("/solutions-builder/");

  // SOLUTIONS BUILDER GETS ITS OWN APP LAYOUT
  // No TopBar
  // No Navbar
  // No Pluma
  // No Footer
  // No CookieBanner
  if (isSolutionsBuilder) {
    return (
      <div className="App solutions-builder-app">
        <Routes location={location} key={location.key}>
          <Route
            path="/solutions-builder"
            element={<SolutionsBuilder />}
          />

          <Route
            path="/solutions-builder/*"
            element={<SolutionsBuilder />}
          />
        </Routes>
      </div>
    );
  }

  // NORMAL GO!FOTON WEBSITE
  return (
    <div className="App">
      <HashRedirector />
      <AnalyticsTracker />
      <HeatmapClickTracker />

      <TopBar />
      <Navbar />
      {!location.pathname.startsWith("/admin") && <Pluma />}

      <Routes location={location} key={location.key}>

        <Route
          path="/"
          element={<Home />}
        />

        <Route
          path="/products"
          element={<AllProducts key={location.key} />}
        />

        <Route
          path="/services"
          element={<Services key={location.key} />}
        />

        <Route
          path="/knowledge-center"
          element={<Resources key={location.key} />}
        />

        <Route
          path="/bead"
          element={<BEAD key={location.key} />}
        />

        <Route
          path="/partners"
          element={<Partners key={location.key} />}
        />

        <Route
          path="/contact-us"
          element={<Contact key={location.key} />}
        />

        <Route
          path="/about"
          element={<About key={location.key} />}
        />

        <Route
          path="/leadership"
          element={<Leadership key={location.key} />}
        />

        <Route
          path="/news"
          element={<News key={location.key} />}
        />

        <Route
          path="/careers"
          element={<Careers key={location.key} />}
        />

        <Route
          path="/login"
          element={<Login />}
        />

        <Route
          path="/portal"
          element={
            <RequirePortalAuth>
              <PortalDashboard />
            </RequirePortalAuth>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminDashboard />
            </RequireAuth>
          }
        />

        {/* BLOGS */}

        <Route
          path="/blog/:slug"
          element={<BlogPost />}
        />

        <Route
          path="/fiber-optic-basics-in-the-age-of-ai-datacenters"
          element={<BlogFiberOpticBasics key={location.key} />}
        />

        <Route
          path="/the-importance-of-fiber-density-in-ai-data-centers"
          element={<BlogAIDataCenters key={location.key} />}
        />

        <Route
          path="/what-are-VSFF-connectors-and-why-should-you-use-them"
          element={<BlogVSFFConnectors key={location.key} />}
        />

        <Route
          path="/decoding-the-gofoton-brand"
          element={<BlogTheGofotonBrand key={location.key} />}
        />

        <Route
          path="/cable-assembly-questions-answered"
          element={<BlogCableAssemblyQuestionsAnswered key={location.key} />}
        />

        <Route
          path="/the-networking-skilled-labor-gap"
          element={<BlogSkilledLaborGap key={location.key} />}
        />

        <Route
          path="/connectivity-guide-to-ai-data-center-terminology"
          element={<BlogAITerminology key={location.key} />}
        />

        {/* SOLUTIONS */}

        <Route
          path="/solutions"
          element={<Solutions key={location.key} />}
        />

        <Route
          path="/solutions/ai"
          element={<SolutionAI key={location.key} />}
        />

        <Route
          path="/solutions/datacenter"
          element={<SolutionDatacenter key={location.key} />}
        />

        <Route
          path="/solutions/central-office"
          element={<SolutionCentralOffice key={location.key} />}
        />

        <Route
          path="/solutions/outside-plant"
          element={<SolutionOutsidePlant key={location.key} />}
        />

        <Route
          path="/solutions/fttx"
          element={<SolutionFTTX key={location.key} />}
        />

        <Route
          path="/solutions/rural-broadband"
          element={<SolutionRuralBroadband key={location.key} />}
        />

        <Route
          path="/solutions/wireless"
          element={<SolutionWireless key={location.key} />}
        />

        <Route
          path="/solutions/enterprise"
          element={<SolutionEnterprise key={location.key} />}
        />

        <Route
          path="/solutions/medical-imaging"
          element={<SolutionMedicalImaging key={location.key} />}
        />

        <Route
          path="/solutions/neuroscience"
          element={<SolutionNeuroscience key={location.key} />}
        />

        <Route
          path="/solutions/specialty-optics"
          element={<SolutionSpecialtyOptics key={location.key} />}
        />

        {/* TRADESHOWS */}

        <Route
          path="/events"
          element={<Events />}
        />

        {/* LANDING PAGES */}

        <Route
          path="/fiber-shuffle"
          element={<FiberShuffle key={location.key} />}
        />

        {/* OTHER */}

        <Route
          path="/privacy-policy"
          element={<PrivacyPolicy key={location.key} />}
        />

        <Route
          path="/site-map"
          element={<SiteMap />}
        />

        <Route
          path="/part-lookup"
          element={<PartNumberBuilder />}
        />

        <Route
          path="/avatar-testing"
          element={<AvatarTesting />}
        />

        <Route
          path="/:slug"
          element={<SlugRouter key={location.key} />}
        />

        <Route
          path="*"
          element={<NotFound />}
        />

      </Routes>

      <Footer />
      <CookieBanner />
    </div>
  );
}

export default App;

export function superscriptRegistered(text) {
  if (!text) return '';

  return text.replace(
    /®/g,
    '<sup style="font-size:0.65em; vertical-align: super;">®</sup>'
  );
}